// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../utils/apiClient';
import { validatePasswordStrength } from '../utils/passwordValidation';

// Styled components (reusing styles from Login/Register components)
const ResetPasswordContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const Logo = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  letter-spacing: 1px;
`;

const ResetPasswordCard = styled.div`
  width: 100%;
  max-width: 500px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[8]};
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ResetButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    margin-left: ${({ theme }) => theme.spacing[1]};
    font-weight: 500;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(10, 255, 112, 0.1);
  color: ${({ theme }) => theme.colors.success};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: rgba(255, 58, 94, 0.1);
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const InfoBox = styled.div`
  background-color: rgba(74, 144, 226, 0.1);
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const PasswordRequirements = styled.ul`
  margin-top: ${({ theme }) => theme.spacing[2]};
  padding-left: ${({ theme }) => theme.spacing[5]};
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing[1]};
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Footer = styled.footer`
  margin-top: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  text-align: center;
`;

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isValidToken, setIsValidToken] = useState(null);
    const [uid, setUid] = useState('');
    const [token, setToken] = useState('');

    const navigate = useNavigate();
    const { uid: urlUid, token: urlToken } = useParams();

    // Function to display a global error message
    const showGlobalError = (message) => {
        // Remove existing error if any
        const existingError = document.getElementById('global-error-popup');
        if (existingError) {
            document.body.removeChild(existingError);
        }

        // Create new error popup element
        const errorPopup = document.createElement('div');
        errorPopup.id = 'global-error-popup';

        // Style the popup
        Object.assign(errorPopup.style, {
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#dc3545',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
            zIndex: '9999',
            maxWidth: '350px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            opacity: '0',
            transform: 'translateX(30px)',
            transition: 'all 0.3s ease'
        });

        // Create close button
        const closeButton = document.createElement('span');
        closeButton.innerHTML = '&times;';
        Object.assign(closeButton.style, {
            marginLeft: '15px',
            fontSize: '20px',
            cursor: 'pointer',
            fontWeight: 'bold'
        });

        // Add close functionality
        closeButton.onclick = function () {
            errorPopup.style.opacity = '0';
            errorPopup.style.transform = 'translateX(30px)';
            setTimeout(() => {
                if (document.body.contains(errorPopup)) {
                    document.body.removeChild(errorPopup);
                }
            }, 300);
        };

        // Set the message
        const messageSpan = document.createElement('span');
        messageSpan.innerText = message;
        errorPopup.appendChild(messageSpan);
        errorPopup.appendChild(closeButton);

        // Add to the DOM
        document.body.appendChild(errorPopup);

        // Trigger animation
        setTimeout(() => {
            errorPopup.style.opacity = '1';
            errorPopup.style.transform = 'translateX(0)';
        }, 10);

        // Auto dismiss after 5 seconds
        setTimeout(() => {
            if (document.body.contains(errorPopup)) {
                errorPopup.style.opacity = '0';
                errorPopup.style.transform = 'translateX(30px)';
                setTimeout(() => {
                    if (document.body.contains(errorPopup)) {
                        document.body.removeChild(errorPopup);
                    }
                }, 300);
            }
        }, 5000);
    };

    // Extract token from URL on component mount
    useEffect(() => {
        if (urlUid && urlToken) {
            setUid(urlUid);
            setToken(urlToken);
            setIsValidToken(true);
        } else {
            setIsValidToken(false);
            setError('Invalid reset link. Please request a new password reset link.');
        }
    }, [urlUid, urlToken]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous messages
        setError('');
        setSuccess('');

        // Front-end validation
        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            showGlobalError('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            showGlobalError('Passwords do not match');
            return;
        }

        // Password strength validation
        const passwordValidation = validatePasswordStrength(password);
        if (!passwordValidation.isValid) {
            const errorMessage = passwordValidation.errors[0];
            setError(errorMessage);
            showGlobalError(errorMessage);
            return;
        }

        try {
            setLoading(true);

            // Create request data matching the backend API
            const requestData = {
                password: password,
                confirm_password: confirmPassword,
                uid: uid,
                token: token
            };

            // Use your API client to send the request
            const response = await api.auth.confirmPasswordReset(requestData);

            // Success case
            if (response && response.detail) {
                setSuccess(response.detail);
                showGlobalError(`Success: ${response.detail}`);

                // Redirect to login page after 3 seconds
                setTimeout(() => {
                    navigate('/login', {
                        state: { message: 'Your password has been reset. Please login with your new password.' }
                    });
                }, 3000);
            }
        } catch (err) {
            // Extract error message
            let errorMessage = 'An error occurred while resetting your password';

            // Handle response format based on your API
            if (err.response && err.response.data) {
                if (err.response.data.detail) {
                    errorMessage = err.response.data.detail;

                    // Handle specific error cases from backend
                    if (errorMessage.includes('expired')) {
                        errorMessage = 'Your password reset link has expired. Please request a new one.';
                        setIsValidToken(false);
                    } else if (errorMessage.includes('invalid')) {
                        errorMessage = 'Invalid reset link. Please request a new password reset link.';
                        setIsValidToken(false);
                    }
                }
            }

            setError(errorMessage);
            showGlobalError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // If token is invalid, show error page
    if (isValidToken === false) {
        return (
            <ResetPasswordContainer>
                <Logo>C.H.I.E.F</Logo>

                <ResetPasswordCard>
                    <Title>Invalid Reset Link</Title>
                    <Subtitle>Your password reset link is invalid or has expired</Subtitle>

                    <InfoBox>
                        For security reasons, password reset links expire after a certain time. Please request a new password reset link.
                    </InfoBox>

                    <ResetButton onClick={() => navigate('/forgot-password')}>
                        Request New Reset Link
                    </ResetButton>

                    <LoginLink>
                        Remember your password?<Link to="/login">Login</Link>
                    </LoginLink>
                </ResetPasswordCard>

                <Footer>
                    © {new Date().getFullYear()} C.H.I.E.F. All rights reserved.
                </Footer>
            </ResetPasswordContainer>
        );
    }

    // Display reset form if parameters are valid
    return (
        <ResetPasswordContainer>
            <Logo>C.H.I.E.F</Logo>

            <ResetPasswordCard>
                <Title>Reset Your Password</Title>
                <Subtitle>Choose a new secure password for your account</Subtitle>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                <InfoBox>
                    Create a strong password that you don't use for other websites.
                    <PasswordRequirements>
                        <li>At least 8 characters long</li>
                        <li>Include a mix of letters, numbers, and symbols</li>
                        <li>Avoid using easily guessable information</li>
                    </PasswordRequirements>
                </InfoBox>

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="password">New Password</Label>
                        <PasswordInputWrapper>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                placeholder="Enter your new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength="8"
                            />
                            <PasswordToggle
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                            </PasswordToggle>
                        </PasswordInputWrapper>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <PasswordInputWrapper>
                            <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                placeholder="Confirm your new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <PasswordToggle
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                title={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                                {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                            </PasswordToggle>
                        </PasswordInputWrapper>
                    </FormGroup>

                    <ResetButton type="submit" disabled={loading || success}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </ResetButton>

                    <LoginLink>
                        Remember your password?<Link to="/login">Login</Link>
                    </LoginLink>
                </Form>
            </ResetPasswordCard>

            <Footer>
                © {new Date().getFullYear()} C.H.I.E.F. All rights reserved.
            </Footer>
        </ResetPasswordContainer>
    );
};

export default ResetPassword;